import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo } from 'ember-data/relationships';
import { isPresent } from '@ember/utils';

import ActionModel from 'cio/models/action';
import CampaignModel from 'cio/models/campaign';
import NewsletterModel from 'cio/models/newsletter';
import TemplateModel from 'cio/models/template';
import TransactionalMessageModel from 'cio/models/transactional-message';

export enum MessageLibraryItemStatus {
  Active = 'Active',
  Automatic = 'Sends automatically',
  Draft = 'Queues draft',
  Drafted = 'Drafted',
  Off = 'Will not send',
  Sent = 'Sent',
  // non-message items return an empty status
  Empty = '',
}

export enum MessageLibraryItemDeliveryType {
  Email = 'email',
  Push = 'push',
  Slack = 'slack',
  Twilio = 'twilio',
  InApp = 'in_app',
  Unset = 'unset',
  Webhook = 'webhook',
  CreateEvent = 'createevent',
  AttributeUpdate = 'attributeupdate',
  BatchUpdate = 'batchupdate',
}

export enum MatchedField {
  Body = 'body',
  FromAddress = 'from_address',
  ReplyTo = 'reply_to',
  BCC = 'bcc',
  Name = 'name',
  Subject = 'subject',
}

export default class MessageLibraryItemModel extends Model {
  @attr('string') deliveryType!: MessageLibraryItemDeliveryType;
  @attr('string') highlight!: string;
  @attr('string') name!: string;
  @attr('string') status!: MessageLibraryItemStatus;
  @attr('string') subject!: string;
  @attr('number') createdAt!: number;
  @attr('number') updatedAt!: number;
  @attr('string') usedIn!: string;
  @attr('array') fieldsMatched!: MatchedField[];
  @attr('string') fromAddress!: string;
  @attr('string') replyTo!: string;
  @attr('string') bcc!: string;

  @belongsTo('action', { async: false, inverse: null }) action!: ActionModel;
  @belongsTo('campaign', { async: false, inverse: null }) campaign!: CampaignModel;
  @belongsTo('newsletter', { async: false, inverse: null }) newsletter!: NewsletterModel;
  @belongsTo('template', { async: false, inverse: null }) template!: TemplateModel;
  @belongsTo('transactional-message', { async: false, inverse: null }) transactionalMessage!: TransactionalMessageModel;

  get actionId() {
    return (this as any).belongsTo('action').id();
  }
  get campaignId() {
    return (this as any).belongsTo('campaign').id();
  }
  get newsletterId() {
    return (this as any).belongsTo('newsletter').id();
  }
  get templateId() {
    return (this as any).belongsTo('template').id();
  }
  get transactionalMessageId() {
    return (this as any).belongsTo('transactionalMessage').id();
  }

  get belongsToCampaign() {
    return isPresent(this.campaignId);
  }
  get belongsToNewsletter() {
    return isPresent(this.newsletterId);
  }
  get belongsToTransactional() {
    return isPresent(this.transactionalMessageId);
  }

  get parentModelName() {
    if (this.belongsToCampaign) {
      return 'campaign';
    } else if (this.belongsToNewsletter) {
      return 'newsletter';
    } else if (this.belongsToTransactional) {
      return 'transactional-api';
    } else {
      return undefined;
    }
  }
  get parentModelId() {
    if (this.belongsToCampaign) {
      return this.campaignId;
    } else if (this.belongsToNewsletter) {
      return this.newsletterId;
    } else if (this.belongsToTransactional) {
      return this.transactionalMessageId;
    } else {
      return undefined;
    }
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'message-library-item': MessageLibraryItemModel;
  }
}
